import type { PostEditSchema } from '@repo/schemas/post/postEdit';
import type { FilePublic } from '@repo/types/files';
import type { PostMutationOptions, PostPublic } from '@repo/types/posts';

import { standardSchemaResolver } from '@hookform/resolvers/standard-schema';
import { useDeletePostFile } from '@repo/api/post/useDeletePostFile';
import { useEditPost } from '@repo/api/post/useEditPost';
import { useGetPost } from '@repo/api/post/useGetPost';
import { baseErrorMessages } from '@repo/lib/messages/baseErrorMessages';
import { postEditSchema } from '@repo/schemas/post/postEdit';
import { BookText, CheckCircle2Icon, ChevronDownIcon, ChevronLeftIcon, ClockFadingIcon } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';

import type { PostAudienceTab as PostAudienceTabValue } from '@/hooks/posts';

import EditPostsPublishedAtDialog from '@/components/features/manage/manage-posts/EditPostsPublishedAtDialog';
import { PostAudienceTab } from '@/components/features/manage/manage-posts/PostAudienceTab';
import { PostFormFields } from '@/components/features/manage/manage-posts/PostFormFields';
import { Button, ButtonGroup } from '@/components/ui/button';
import { Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuGroup, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { FeaturedIcon } from '@/components/ui/featured-icon';
import { Form } from '@/components/ui/form';
import { LoadingOverlay, LoadingOverlayLabel, LoadingOverlaySpinner } from '@/components/ui/loading-overlay';
import { LoadingSwap } from '@/components/ui/loading-swap';
import { Tabs, TabsContent } from '@/components/ui/tabs';
import { usePostDialog } from '@/hooks/posts';
import { toasterMessageHandler } from '@/lib/toaster-message-handler';

/**
 * Props for the EditPost component.
 * @property {boolean} [open] - Whether the dialog is open.
 * @property {(bool: boolean) => void} [onOpenChange] - Callback to change dialog open state.
 * @property {PostPublic} [post] - The post to edit.
 * @property {PostMutationOptions} [options] - The options for the post.
 */
interface PostEditProps {
  open?: boolean;
  onOpenChange?: (bool: boolean) => void;
  post?: PostPublic;
  options?: PostMutationOptions;
}

export default function EditPost(postEditFormProps: PostEditProps) {
  const { onOpenChange, open, post, options } = postEditFormProps;
  const [currentFiles, setCurrentFiles] = useState<FilePublic[]>([]);
  const [openPublishDialog, setOpenPublishDialog] = useState<boolean>(false);
  const { data: postData, ...postResults } = useGetPost(post?.id, open);
  const parsedPostData = postData ? postEditSchema.safeParse(postData) : undefined;

  const form = useForm<PostEditSchema>({
    mode: 'onBlur',
    defaultValues: {
      name: '',
      content: '',
      active: true,
      attachments: undefined,
      publishedAt: null,
    },
    values: parsedPostData?.success ? parsedPostData.data : undefined,
    resolver: standardSchemaResolver(postEditSchema),
  });

  const globalValue = useWatch({ control: form.control, name: 'global' });

  const {
    tabs,
    setTabs,
    isMainTab,
    audience,
    searchGroupValue,
    setSearchGroupValue,
    selectedGroups,
    setSelectedGroups,
    onRemoveGroup,
    files,
    onAddFiles: hookOnAddFiles,
    onRemoveFile: hookOnRemoveFile,
    resetDialog,
    groups,
    groupsIsEmpty,
    groupsNoResults,
    groupsErrorMessage,
    getGroupsInfinite,
  } = usePostDialog(globalValue);

  const { mutateAsync: editPost, isPending } = useEditPost(options);
  const { mutateAsync: deletePostFile } = useDeletePostFile(postData?.id);

  const onRemoveFile = (id: string | number) => {
    hookOnRemoveFile(id);
    setCurrentFiles((curr) => curr.filter((file) => file.id !== id));
  };

  const calculateMemberChanges = (groupIds: number[]) => {
    const existingGroupIds = (postData?.groups || []).map((group) => group.id);
    const groupsToAdd = groupIds.filter((groupId) => !existingGroupIds.includes(groupId));
    const groupsToRemove = existingGroupIds.filter((groupId) => !groupIds.includes(groupId));

    return [
      {
        groupIds: groupsToAdd,
        add: true,
        remove: false,
      },
      {
        groupIds: groupsToRemove,
        add: false,
        remove: true,
      },
    ];
  };

  const submitPostEdit = async (data: PostEditSchema, overrides?: Partial<PostEditSchema>) => {
    let deletePromises: Promise<string>[] = [];
    if (postData?.files) {
      const filesToDelete = postData.files.filter((file) => !currentFiles.some((curr) => curr.id === file.id));
      deletePromises = filesToDelete.map((file) => deletePostFile(file.id));
    }

    const attachments = files.map(({ file }) => file);
    const editPostPromise = editPost({ ...data, attachments, memberChanges: !data.global ? calculateMemberChanges(selectedGroups.map((group) => group.id)) : undefined, ...overrides });

    Promise.all([...deletePromises, editPostPromise])
      .then(() => {
        onClose();
      })
      .catch((error) => {
        toasterMessageHandler(baseErrorMessages, error);
      });
  };

  const onSubmit = (data: PostEditSchema) => {
    submitPostEdit(data);
  };

  const onSubmitNow = (data: PostEditSchema) => {
    submitPostEdit(data, { publishedAt: new Date().toISOString() });
  };

  const onSubmitPublishedAt = (data: Pick<PostEditSchema, 'publishedAt'>) => {
    form.setValue('publishedAt', data.publishedAt);
    form.handleSubmit(onSubmit)();
  };

  const onClose = () => {
    onOpenChange?.(false);
    resetDialog();
    form.reset();
  };

  useEffect(() => {
    if (!postResults.isSuccess || !open) {
      return;
    }

    setCurrentFiles(postData?.files ?? []);
    setSelectedGroups(postData?.groups ?? []);
  }, [form, open, postData, postResults.isSuccess, setSelectedGroups]);

  useEffect(() => {
    if (postResults.isError) {
      onClose();
      toasterMessageHandler(baseErrorMessages, postResults.error);
    }
  }, [postResults.isError, postResults.error, onClose]);

  return (
    <>
      <EditPostsPublishedAtDialog open={openPublishDialog} onOpenChange={setOpenPublishDialog} publishedAt={postData?.publishedAt} onSubmit={onSubmitPublishedAt} />
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent onCloseAutoFocus={onClose} onEscapeKeyDown={onClose} className={'sm:h-full sm:max-h-206 sm:max-w-240'} isClosable={isMainTab}>
          <Form {...form}>
            <DialogBody>
              <LoadingOverlay isLoading={isPending}>
                <LoadingOverlaySpinner />
                <LoadingOverlayLabel>Trwa edycja postu...</LoadingOverlayLabel>
              </LoadingOverlay>
              <LoadingOverlay isLoading={postResults.isFetching}>
                <LoadingOverlaySpinner />
                <LoadingOverlayLabel>Trwa ładowanie danych...</LoadingOverlayLabel>
              </LoadingOverlay>
              <DialogHeader className={'border-border-primary bg-card items-center gap-2 border-b'}>
                {isMainTab ? (
                  <>
                    <FeaturedIcon size={'lg'}>
                      <BookText />
                    </FeaturedIcon>
                    <div className={'flex min-w-0 grow flex-col gap-0.5'}>
                      <DialogTitle>Edytuj Post</DialogTitle>
                      <DialogDescription className={'text-muted-foreground text-xs'}>Edytuj opis postu</DialogDescription>
                    </div>
                  </>
                ) : (
                  <>
                    <Button variant={'outline'} onClick={() => setTabs('main')}>
                      <ChevronLeftIcon />
                    </Button>
                    <div className={'flex min-w-0 grow flex-col gap-0.5'}>
                      <DialogTitle>Odbiorcy</DialogTitle>
                      <DialogDescription className={'text-muted-foreground text-xs'}>Wybierz grupy odbiorców dla posta</DialogDescription>
                    </div>
                  </>
                )}
              </DialogHeader>
              <Tabs value={tabs} onValueChange={(tab) => setTabs(tab as PostAudienceTabValue)} className={'flex grow flex-col gap-3 overflow-auto'}>
                <TabsContent value={'main'} className={'flex grow flex-col gap-3 p-4'}>
                  <form className={'flex grow flex-col gap-3'} onSubmit={form.handleSubmit(onSubmit)} noValidate>
                    <PostFormFields
                      files={files}
                      currentFiles={currentFiles}
                      onRemoveFile={onRemoveFile}
                      onAddFiles={hookOnAddFiles}
                      author={postData?.creator}
                      audience={audience}
                      onOpenAudience={() => setTabs('audience')}
                      selectedGroups={selectedGroups}
                    />
                  </form>
                </TabsContent>
                <TabsContent value={'audience'} className={'flex grow flex-col gap-3 p-4'}>
                  <PostAudienceTab
                    audience={audience}
                    onAudienceChange={(value) => {
                      form.setValue('global', value === 'public', {
                        shouldDirty: true,
                        shouldTouch: true,
                        shouldValidate: true,
                      });
                    }}
                    searchGroupValue={searchGroupValue}
                    setSearchGroupValue={setSearchGroupValue}
                    selectedGroups={selectedGroups}
                    setSelectedGroups={setSelectedGroups}
                    onRemoveGroup={onRemoveGroup}
                    groups={groups}
                    groupsIsEmpty={groupsIsEmpty}
                    groupsNoResults={groupsNoResults}
                    groupsErrorMessage={groupsErrorMessage}
                    getGroupsInfinite={getGroupsInfinite}
                  />
                </TabsContent>
              </Tabs>
            </DialogBody>
            {isMainTab ? (
              <DialogFooter className={'border-border-primary dark:bg-card bg-accent border-t'}>
                <Button onClick={onClose} type={'button'} variant={'tertiary'}>
                  Anuluj
                </Button>
                <ButtonGroup>
                  <Button variant="primary" disabled={isPending} onClick={form.handleSubmit(onSubmitNow)} type={'submit'}>
                    <LoadingSwap isLoading={isPending} className="flex items-center gap-2">
                      <CheckCircle2Icon />
                      Zapisz
                    </LoadingSwap>
                  </Button>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="primary">
                        <ChevronDownIcon className="transition-all group-aria-expanded/button:rotate-180" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align={'end'} className="w-full max-w-48">
                      <DropdownMenuLabel>Zapisz post</DropdownMenuLabel>
                      <DropdownMenuSeparator />
                      <DropdownMenuGroup>
                        <DropdownMenuItem onClick={form.handleSubmit(onSubmitNow)}>
                          <CheckCircle2Icon />
                          Zapisz teraz
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setOpenPublishDialog(true)}>
                          <ClockFadingIcon />
                          Zaplanuj publikację
                        </DropdownMenuItem>
                      </DropdownMenuGroup>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </ButtonGroup>
              </DialogFooter>
            ) : (
              <DialogFooter className={'border-border-primary bg-accent dark:bg-card border-t'}>
                <Button onClick={() => setTabs('main')} type={'button'} variant={'tertiary'}>
                  Zapisz
                </Button>
              </DialogFooter>
            )}
          </Form>
        </DialogContent>
      </Dialog>
    </>
  );
}
